import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import FormWrapper from "../FormWrapper";
import {
  getProvidersList,
  getProviderDetailById,
} from "../../../redux/providersSlice";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { Tab, TabList, Toaster } from "@fluentui/react-components";
import { FormLoader } from "../../../components/Loader/FormLoader";
import { TableListLoader } from "../../../components/Loader/TableListLoader";
import BulkValuation from "../BulkValuation/bulkValuation";
import ListWrapper from "../ListWrapper";
import NoDataFound from "../../../components/NoDataFound/noDataFound";
import { configFm } from "../../../types";
import { convertStringToArray } from "../../../utils";
import { useActiveRecordSync } from "../../../components/useActiveRecordSync";
const FORM_CONTEXT = "MASTER_SETTINGS_PROVIDERS";
const API_BASE_PATH = "/system-manager/providers";
// Shortest search term the provider list will accept; mirrored server side.
const MIN_SEARCH_LENGTH = 3;

function ProviderDetailPage() {
  const { itemId, action } = useParams();
  const location = useLocation();
  const { showToast, toasterId } = useToastMessage();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { activeItem, list, loading, activeItemloading, count } = useSelector(
    (state: RootState) => state.providers
  );
  const [selectedProvider, setSelectedProvider] = useState({});
  const formSettingsObj = useSelector((state: RootState) => state.formSettings);
  const [configForm, setConfigForm] = useState<configFm>();
  const [currentRecord, setCurrentRecord] = useState(1);
  const [activeTab, setActiveTab] = useState("tab1");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  // `searchInput` is what's typed in the box, `globalFilter` is the term that
  // has actually been applied. Keeping them on the page (rather than inside the
  // table) means the keyword survives the list reloading or coming back empty.
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const handleTabChange = (event: any, data: any) => {
    setActiveTab(data.value);
  };
  const canApplySearch = searchInput.trim().length >= MIN_SEARCH_LENGTH;
  // Go back to page 1 so results aren't hidden behind whatever page was open.
  const applySearch = useCallback((filter: string) => {
    setGlobalFilter(filter);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);
  const clearSearch = useCallback(() => {
    setSearchInput("");
    applySearch("");
  }, [applySearch]);
  useEffect(() => {
    dispatch(
      getProvidersList({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: globalFilter,
      })
    );
  }, [dispatch, pagination.pageIndex, pagination.pageSize, globalFilter]);
  useEffect(() => {
    if (list.length > 0 && itemId) {
      const itemInList = list.find((item) => item.id === itemId);
      if (itemInList) {
        dispatch(getProviderDetailById(itemId));
      }
    }
  }, [list, itemId, dispatch, pagination.pageSize]);

  // The effect above only redirected away from a stale/deleted id when
  // `list.length > 0` -- deleting the LAST remaining provider skipped that
  // branch entirely (list.length === 0), leaving the stale form up
  // indefinitely with no redirect. useActiveRecordSync covers both branches:
  // redirect to a real record's own id, or to the bare list route once the
  // list is genuinely empty.
  useActiveRecordSync({
    listItems: list,
    idKey: "id",
    selectedId: itemId,
    loading: loading || action === "add",
    onSelect: (item) =>
      navigate(`${API_BASE_PATH}/${item.id}/${action || "view"}`, {
        replace: true,
      }),
    onEmpty: () => navigate(API_BASE_PATH, { replace: true }),
  });

  useEffect(() => {
    if (list.length > 0 && !itemId && !action) {
      navigate(`${API_BASE_PATH}`);
    }
  }, [list, itemId, action, navigate]);
  useEffect(() => {
    if (
      formSettingsObj?.systemAdmin &&
      formSettingsObj.systemAdmin.formConfig !== undefined
    ) {
      let configObj = formSettingsObj.systemAdmin.formConfig.find(
        (item: any) => item.context === FORM_CONTEXT
      );
      if (configObj) {
        setConfigForm(configObj);
      }
    }
  }, [formSettingsObj]);

  const loadData = async (
    dispatch: AppDispatch,
    id?: string
  ): Promise<void> => {
    await dispatch(
      getProvidersList({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: globalFilter,
      })
    );
    if (id) {
      await dispatch(getProviderDetailById(id));
    }
  };
  const hasListItems = useMemo(() => list.length > 0, [list]);

  const handleCancel = () => {
    setActiveTab("tab1");
  };
  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <TabList selectedValue={activeTab} onTabSelect={handleTabChange}>
        {!convertStringToArray(process.env.REACT_APP_HIDE_CTA_BUTTON).includes(
          "sm_providerDetails"
        ) ? (
          <Tab value="tab1">Provider Details</Tab>
        ) : (
          ""
        )}
        {!convertStringToArray(process.env.REACT_APP_HIDE_CTA_BUTTON).includes(
          "sm_bulk_valuation"
        ) ? (
          <Tab value="tab2">Bulk Valuation</Tab>
        ) : (
          ""
        )}
      </TabList>
      <div className="provider_container_flex">
        {action &&
          (activeItemloading ? (
            <FormLoader />
          ) : (
            <>
              {activeTab === "tab1" && (
                <FormWrapper
                  dataList={list}
                  context={FORM_CONTEXT}
                  reloadData={loadData}
                  selectedRowItem={activeItem}
                  config={configForm}
                  action={action || "view"}
                  apiPath={API_BASE_PATH}
                  notify={showToast}
                  activeRow={currentRecord}
                />
              )}
              {activeTab === "tab2" && (
                <BulkValuation
                  selectedProvider={activeItem}
                  handleCancel={handleCancel}
                />
              )}
            </>
          ))}

        {loading ? (
          <TableListLoader rows={8} columns={6} />
        ) : (
          action &&
          // Keep the table mounted while a search is active even when it
          // matched nothing, otherwise its toolbar — and the keyword in it —
          // would disappear with no way to clear the term.
          (hasListItems || globalFilter !== "" ? (
            <ListWrapper
              data={list}
              context={FORM_CONTEXT}
              selectedRowItem={activeItem}
              onRowAction={(action, row) =>
                navigate(`${API_BASE_PATH}/${row?.id}/${action || "view"}`)
              }
              currentRecord={(index) => setCurrentRecord(index)}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={count}
              search={{
                input: searchInput,
                applied: globalFilter,
                minLength: MIN_SEARCH_LENGTH,
                canApply: canApplySearch,
                onInputChange: setSearchInput,
                onApply: () => applySearch(searchInput.trim()),
                onClear: clearSearch,
              }}
            />
          ) : (
            <NoDataFound />
          ))
        )}
      </div>
    </>
  );
}

export default ProviderDetailPage;
